import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';
import { formatDate } from '../../utils/dateUtils';

interface MatchGroup {
  id: string;
  name: string;
  matchWeek: number;
  matchCount: number;
}

interface Match {
  id: string;
  matchDate: string;
  homeTeam: {
    name: string;
    shortName?: string;
  };
  awayTeam: {
    name: string;
    shortName?: string;
  };
  matchGroup?: {
    name: string;
    season: {
      name: string;
    };
  };
}

interface SeasonDistribution {
  id: string;
  name: string;
  matchGroups: MatchGroup[];
  matchesWithNoGroup: Match[];
  matchesWithWrongGroup: Match[];
}

const AdminMatchDistribution: React.FC = () => {
  const [distribution, setDistribution] = useState<SeasonDistribution[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSeasons, setExpandedSeasons] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchDistribution();
  }, []);

  const fetchDistribution = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/match-distribution');
      setDistribution(response.data);
      
      // Expand all seasons by default
      const expanded: Record<string, boolean> = {};
      response.data.forEach((season: SeasonDistribution) => {
        expanded[season.id] = true;
      });
      setExpandedSeasons(expanded);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load match distribution');
    } finally {
      setLoading(false);
    }
  };

  const toggleSeasonExpand = (seasonId: string) => {
    setExpandedSeasons(prev => ({
      ...prev,
      [seasonId]: !prev[seasonId]
    }));
  };

  const getTotalMatches = (season: SeasonDistribution) => {
    return season.matchGroups.reduce((sum, mg) => sum + mg.matchCount, 0);
  };

  const hasIssues = (season: SeasonDistribution) => {
    return season.matchesWithNoGroup.length > 0 || season.matchesWithWrongGroup.length > 0;
  };

  const getExpectedMatchCount = (seasonName: string): number => {
    // Premier League has 10 matches per week (20 teams)
    if (seasonName.toLowerCase().includes('premier league')) {
      return 10;
    }
    // Most other leagues have 12 matches per week (24 teams)
    return 12;
  };

  const getMatchGroupColor = (matchCount: number, expectedCount: number) => {
    if (matchCount === 0) {
      return {
        borderColor: '#e5e7eb',
        backgroundColor: 'transparent',
        isWarning: false
      };
    }
    
    if (matchCount === expectedCount) {
      // Correct number - green/highlight
      return {
        borderColor: 'var(--highlight)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        isWarning: false
      };
    }
    
    // Non-standard number - orange warning
    return {
      borderColor: '#f97316',
      backgroundColor: 'rgba(249, 115, 22, 0.1)',
      isWarning: true
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading match distribution...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600 text-xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <Link to="/admin" className="mb-4 inline-block" style={{ color: 'var(--highlight)' }}>
          ← Back to Admin Dashboard
        </Link>
        <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Match Distribution
        </h1>
        <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
          View match counts per match week and identify configuration issues
        </p>
      </div>

      {distribution.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-xl" style={{ color: 'var(--text-secondary)' }}>
            No active seasons found
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {distribution.map(season => (
            <div key={season.id} className="card rounded-lg shadow">
              {/* Season Header */}
              <div 
                className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                onClick={() => toggleSeasonExpand(season.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                      {season.name}
                    </h2>
                    {hasIssues(season) && (
                      <span className="px-3 py-1 text-sm bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 rounded-full font-medium">
                        Issues Detected
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        Total Matches
                      </p>
                      <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                        {getTotalMatches(season)}
                      </p>
                    </div>
                    <svg 
                      className={`w-6 h-6 transition-transform ${expandedSeasons[season.id] ? 'rotate-180' : ''}`}
                      style={{ color: 'var(--text-secondary)' }}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedSeasons[season.id] && (
                <div className="border-t border-gray-200 dark:border-gray-700 p-6">
                  {/* Match Groups Grid */}
                  <div className="mb-6">
                    <div className="mb-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                          Match Weeks
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-xs">
                          <div className="flex items-center space-x-1">
                            <div className="w-3 h-3 rounded border-2" style={{ borderColor: 'var(--highlight)', backgroundColor: 'rgba(34, 197, 94, 0.1)' }}></div>
                            <span style={{ color: 'var(--text-secondary)' }}>Correct</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div className="w-3 h-3 rounded border-2" style={{ borderColor: '#f97316', backgroundColor: 'rgba(249, 115, 22, 0.1)' }}></div>
                            <span style={{ color: 'var(--text-secondary)' }}>Non-standard</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <div className="w-3 h-3 rounded border-2" style={{ borderColor: '#e5e7eb' }}></div>
                            <span style={{ color: 'var(--text-secondary)' }}>Empty</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                      {season.matchGroups.map(matchGroup => {
                        const expectedCount = getExpectedMatchCount(season.name);
                        const colors = getMatchGroupColor(matchGroup.matchCount, expectedCount);
                        
                        return (
                          <div 
                            key={matchGroup.id}
                            className="p-4 rounded-lg border-2 relative"
                            style={{
                              borderColor: colors.borderColor,
                              backgroundColor: colors.backgroundColor
                            }}
                          >
                            <div className="text-center">
                              <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                                Week {matchGroup.matchWeek}
                              </p>
                              <p className="text-xs mb-2 truncate" style={{ color: 'var(--text-secondary)' }}>
                                {matchGroup.name}
                              </p>
                              <p className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
                                {matchGroup.matchCount}
                              </p>
                              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                {matchGroup.matchCount === 1 ? 'match' : 'matches'}
                              </p>
                              {colors.isWarning && matchGroup.matchCount > 0 && (
                                <p className="text-xs mt-1 font-medium" style={{ color: '#f97316' }}>
                                  Expected: {expectedCount}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {season.matchGroups.length === 0 && (
                      <p className="text-center py-4" style={{ color: 'var(--text-secondary)' }}>
                        No active match groups found for this season
                      </p>
                    )}
                  </div>

                  {/* Matches with No Group */}
                  {season.matchesWithNoGroup.length > 0 && (
                    <div className="mb-6">
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 mb-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                              {season.matchesWithNoGroup.length} {season.matchesWithNoGroup.length === 1 ? 'match' : 'matches'} with no match group assigned
                            </h3>
                          </div>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Match
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {season.matchesWithNoGroup.map(match => (
                              <tr key={match.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 dark:text-white">
                                    {match.homeTeam.name} vs {match.awayTeam.name}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {formatDate(match.matchDate)}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <Link 
                                    to={`/admin/matches?highlight=${match.id}`}
                                    className="text-highlight hover:underline"
                                  >
                                    Fix →
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Matches with Wrong Group */}
                  {season.matchesWithWrongGroup.length > 0 && (
                    <div className="mb-6">
                      <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4 mb-4">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                              {season.matchesWithWrongGroup.length} {season.matchesWithWrongGroup.length === 1 ? 'match' : 'matches'} with incorrect match group
                            </h3>
                          </div>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Match
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Current Match Group
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {season.matchesWithWrongGroup.map(match => (
                              <tr key={match.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 dark:text-white">
                                    {match.homeTeam.name} vs {match.awayTeam.name}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-red-600 dark:text-red-400">
                                    {match.matchGroup?.name}
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      ({match.matchGroup?.season.name})
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {formatDate(match.matchDate)}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <Link 
                                    to={`/admin/matches?highlight=${match.id}`}
                                    className="text-highlight hover:underline"
                                  >
                                    Fix →
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* All Good Message */}
                  {!hasIssues(season) && (
                    <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-green-800 dark:text-green-200">
                            All matches are correctly assigned to match groups
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMatchDistribution;

